/**
 * ProfessionalScheduleWizard Component
 * 3-step wizard for configuring professional schedules
 *
 * Step 1: Select working days
 * Step 2: Select time slots (default for all days)
 * Step 3: Adjust per day if needed
 *
 * @version 2025.1.0
 */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Check,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import WeekDaySelector from './WeekDaySelector';
import TimeSlotGrid from './TimeSlotGrid';
import {
  WeekDay,
  WEEK_DAY_NAMES,
  TimeSlot,
  BulkScheduleFormData,
  ProfessionalForSchedule,
  ClinicForSchedule,
  ProfessionalSchedule,
} from '../../../types/professionalSchedule';

interface ProfessionalScheduleWizardProps {
  professional: ProfessionalForSchedule;
  clinic: ClinicForSchedule;
  onComplete: (data: BulkScheduleFormData) => Promise<void>;
  onCancel: () => void;
  existingSchedules?: ProfessionalSchedule[];
}

interface DaySchedule {
  weekDay: WeekDay;
  slots: TimeSlot[];
  expanded: boolean;
}

const steps = [
  'Dias de Trabalho',
  'Horários Padrão',
  'Ajustes por Dia',
];

/**
 * Helper function to generate individual time slots from a time range
 * Used to convert existing schedule records (which may be ranges) to individual slots
 */
const generateSlotsFromRange = (
  startTime: string,
  endTime: string,
  slotDuration: number
): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  let currentMinutes = (startHour ?? 0) * 60 + (startMin ?? 0);
  const endMinutes = (endHour ?? 0) * 60 + (endMin ?? 0);

  while (currentMinutes < endMinutes) {
    const slotStartHour = Math.floor(currentMinutes / 60);
    const slotStartMin = currentMinutes % 60;
    const nextMinutes = currentMinutes + slotDuration;
    const slotEndHour = Math.floor(nextMinutes / 60);
    const slotEndMin = nextMinutes % 60;

    slots.push({
      startTime: `${String(slotStartHour).padStart(2, '0')}:${String(slotStartMin).padStart(2, '0')}`,
      endTime: `${String(slotEndHour).padStart(2, '0')}:${String(slotEndMin).padStart(2, '0')}`,
      selected: true,
    });

    currentMinutes = nextMinutes;
  }

  return slots;
};

/**
 * Helper function to extract all unique slots from existing schedules
 * Converts schedule records (which may be ranges) to individual slots for the grid
 */
const extractSlotsFromSchedules = (
  schedules: ProfessionalSchedule[],
  slotDuration: number
): TimeSlot[] => {
  const slotMap = new Map<string, TimeSlot>();

  for (const schedule of schedules) {
    // Generate individual slots from the schedule range
    const slots = generateSlotsFromRange(schedule.startTime, schedule.endTime, slotDuration);
    for (const slot of slots) {
      const key = `${slot.startTime}-${slot.endTime}`;
      if (!slotMap.has(key)) {
        slotMap.set(key, slot);
      }
    }
  }

  // Sort by start time
  return Array.from(slotMap.values()).sort((a, b) =>
    a.startTime.localeCompare(b.startTime)
  );
};

const ProfessionalScheduleWizard: React.FC<ProfessionalScheduleWizardProps> = ({
  professional,
  clinic,
  onComplete,
  onCancel,
  existingSchedules = [],
}) => {
  const slotDuration = professional.defaultAppointmentDuration ?? clinic.defaultSlotDuration ?? 30;

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Selected days
  const [selectedDays, setSelectedDays] = useState<WeekDay[]>(() => {
    if (existingSchedules.length > 0) {
      const days = new Set(existingSchedules.map(s => s.weekDay));
      return Array.from(days).sort((a, b) => a - b);
    }
    return [WeekDay.MONDAY, WeekDay.TUESDAY, WeekDay.WEDNESDAY, WeekDay.THURSDAY, WeekDay.FRIDAY];
  });

  const [irregularSchedule, setIrregularSchedule] = useState(false);

  // Step 2: Default time slots (initialized from existing schedules if available)
  const [defaultSlots, setDefaultSlots] = useState<TimeSlot[]>(() => {
    if (existingSchedules.length > 0) {
      return extractSlotsFromSchedules(existingSchedules, slotDuration);
    }
    return [];
  });

  // Step 3: Per-day schedules
  const [daySchedules, setDaySchedules] = useState<DaySchedule[]>([]);

  // Initialize day schedules when moving to step 3
  const initializeDaySchedules = () => {
    const schedules: DaySchedule[] = selectedDays.map(day => {
      // Always use the defaultSlots from step 2, which reflects the user's current selection
      // The defaultSlots were already initialized from existing schedules and may have been modified by the user
      return {
        weekDay: day,
        slots: defaultSlots.map(slot => ({ ...slot })), // Deep copy to allow per-day modifications
        expanded: false,
      };
    });
    setDaySchedules(schedules);
  };

  const handleNext = () => {
    setError(null);

    if (activeStep === 0) {
      if (selectedDays.length === 0) {
        setError('Selecione pelo menos um dia da semana');
        return;
      }

      if (irregularSchedule) {
        // Skip step 2 and go directly to step 3
        initializeDaySchedules();
        setActiveStep(2);
        return;
      }
    }

    if (activeStep === 1) {
      if (defaultSlots.length === 0) {
        setError('Selecione pelo menos um horário');
        return;
      }
      initializeDaySchedules();
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);

    if (activeStep === 2 && irregularSchedule) {
      // Go back to step 1 if irregular schedule
      setActiveStep(0);
      return;
    }

    setActiveStep((prev) => prev - 1);
  };

  const handleDayExpand = (weekDay: WeekDay) => {
    setDaySchedules(prev =>
      prev.map(ds =>
        ds.weekDay === weekDay ? { ...ds, expanded: !ds.expanded } : ds
      )
    );
  };

  const handleDaySlotsChange = (weekDay: WeekDay, slots: TimeSlot[]) => {
    setDaySchedules(prev =>
      prev.map(ds =>
        ds.weekDay === weekDay ? { ...ds, slots } : ds
      )
    );
  };

  const handleComplete = async () => {
    setError(null);
    setLoading(true);

    try {
      // Build the schedule data
      const schedules: BulkScheduleFormData['schedules'] = [];

      for (const daySchedule of daySchedules) {
        if (daySchedule.slots.length === 0) continue;

        // Each slot becomes an individual schedule record
        for (const slot of daySchedule.slots) {
          schedules.push({
            weekDay: daySchedule.weekDay,
            startTime: slot.startTime,
            endTime: slot.endTime,
          });
        }
      }

      if (schedules.length === 0) {
        setError('Nenhum horário configurado. Configure pelo menos um horário.');
        setLoading(false);
        return;
      }

      const data: BulkScheduleFormData = {
        professionalId: professional.id,
        clinicId: clinic.id,
        schedules,
        slotDuration,
      };

      await onComplete(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar agenda';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Summary of selected schedule
  const scheduleSummary = useMemo(() => {
    let totalSlots = 0;
    daySchedules.forEach(ds => {
      totalSlots += ds.slots.length;
    });
    return {
      days: daySchedules.filter(ds => ds.slots.length > 0).length,
      totalSlots,
    };
  }, [daySchedules]);

  const renderStep1 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Quais dias da semana o profissional atende nesta clínica?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Selecione todos os dias em que <strong>{professional.name}</strong> atende em <strong>{clinic.tradeName}</strong>.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Duração do slot: <strong>{slotDuration} min</strong>
        {professional.defaultAppointmentDuration
          ? ' (configurado no cadastro do profissional)'
          : ' (padrão da clínica)'}
      </Alert>

      <WeekDaySelector
        selectedDays={selectedDays}
        onChange={setSelectedDays}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={irregularSchedule}
            onChange={(e) => setIrregularSchedule(e.target.checked)}
          />
        }
        label="Escala irregular (horários diferentes em cada dia)"
        sx={{ mt: 3 }}
      />

      {irregularSchedule && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Você configurará os horários individualmente para cada dia na próxima etapa.
        </Alert>
      )}
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Quais horários o profissional atende?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Estes horários serão aplicados para:{' '}
        <strong>{selectedDays.map(d => WEEK_DAY_NAMES[d]).join(', ')}</strong>
      </Typography>

      <TimeSlotGrid
        slotDuration={slotDuration}
        selectedSlots={defaultSlots}
        onChange={setDefaultSlots}
      />
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Ajuste os horários por dia (se necessário)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Clique em um dia para expandir e ajustar os horários específicos.
      </Typography>

      {daySchedules.map((daySchedule) => (
        <Card key={daySchedule.weekDay} sx={{ mb: 2 }}>
          <CardContent sx={{ pb: daySchedule.expanded ? 2 : '16px !important' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
              onClick={() => handleDayExpand(daySchedule.weekDay)}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {WEEK_DAY_NAMES[daySchedule.weekDay]}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {daySchedule.slots.length} horário(s) selecionado(s)
                </Typography>
              </Box>
              <IconButton size="small">
                {daySchedule.expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            <Collapse in={daySchedule.expanded}>
              <Divider sx={{ my: 2 }} />
              <TimeSlotGrid
                slotDuration={slotDuration}
                selectedSlots={daySchedule.slots}
                onChange={(slots) => handleDaySlotsChange(daySchedule.weekDay, slots)}
              />
            </Collapse>
          </CardContent>
        </Card>
      ))}

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Resumo:</strong> {scheduleSummary.days} dia(s) com {scheduleSummary.totalSlots} horário(s) no total.
        </Typography>
      </Alert>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label, index) => (
          <Step key={label} completed={activeStep > index}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ minHeight: 300 }}>
        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0 || loading}
            startIcon={<ArrowBack />}
          >
            Voltar
          </Button>

          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForward />}
              disabled={loading}
            >
              Próximo
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleComplete}
              endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Check />}
              disabled={loading}
              color="success"
            >
              {loading ? 'Salvando...' : 'Salvar Agenda'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProfessionalScheduleWizard;
